// CfoCashWeekDrill — the detail panel behind a treasurer-table cell.
//
// Rows are grouped by PROVENANCE, because the affordances differ:
//   · document  — a real open invoice or bill. Its three lag-quantile legs are
//                 merged into one named row, and a move re-times the WHOLE
//                 document (the server suppresses every leg across all 13
//                 weeks, so sending one cell's subtotal would delete the rest).
//   · recurring — a detected stream (payroll, rent, insurance). Movable per
//                 OCCURRENCE via the date-free stream_key: moving August's
//                 payday leaves September's alone.
//   · user      — something a person entered or moved. Always editable, and
//                 resettable back to the model's own timing.
//   · modeled   — the aggregate residual. Collapsed into ONE card per cell:
//                 it has no document id, so an expectation against it would be
//                 additive rather than a re-timing, and thirty-three identical
//                 anonymous rows is the complaint, not the feature.
//
// Available on every plan — decided 2026-07-14 (scenarios stay Pro).

import { useEffect, useMemo, useState } from 'react'
import type {
  QuickBooksCashPlanningDetailsResponse,
  QuickBooksCashPlanningRow,
  ServerApiClient,
} from '../../../data/ServerApiClient'
import { cashPlanningDocumentIdFor, documentTotalFor } from './cfoCashDocument'
import {
  cashRowProvenance,
  cashRowWarnings,
  describeCashRow,
  describeModeledPlug,
} from './cfoCashEvidence'
import { signedCashAmount, type CashTableCategoryKey } from './cfoCashTableModel'
import { expectationSourceTypeFor, type CashPlanningMove } from './cfoCashPlanProjection'
import { formatCurrencyCompact, trimStr } from './cfoFormat'

export type CfoCashWeekDrillProps = {
  appApi: ServerApiClient
  workbookId: string | null
  // Only the nine category cells are clickable — totals, balances and the
  // new section subtotals are not — so the drill always opens on a bucket.
  rowKey: CashTableCategoryKey
  rowLabel: string
  weekStart: string
  weekLabel: string
  rows: QuickBooksCashPlanningRow[]
  /** Every row in the plan, across all weeks — needed to move a whole document. */
  allRows?: QuickBooksCashPlanningRow[]
  /** Open forecast weeks a row can be moved to. Closed weeks are excluded: they
   * are posted actuals and re-timing into them would rewrite history. */
  weekOptions?: { weekStart: string; label: string }[]
  canEdit?: boolean
  scenarioKey?: string
  /** Immutable summary receipt used to load paginated transaction evidence for
   * posted-actual cells. Forecast/user rows remain available immediately from
   * the summary payload and never wait on this optional drill request. */
  summaryId?: string | null
  currentRead?: {
    as_of_date: string
    scenario_key: string
    history_weeks: number
    forecast_weeks: number
    due_date_assumption: boolean
  }
  detailHandle?: {
    row_count: number
    summary_amount: string
  } | null
  onMove?: (move: CashPlanningMove) => Promise<void>
  /** Undo: drops the user's expectation so the model's own rows come back. */
  onReset?: (expectationId: string) => Promise<void>
  onChanged?: () => void
  onClose?: () => void
}

type DrillGroup = {
  key: string
  title: string
  /** What lands in THIS cell. */
  amount: number
  /** The whole document across every week, when this row is document-backed. */
  documentTotal: number | null
  detail: string
  chips: string[]
  warnings: string[]
  editable: boolean
  documentId: string | null
  sourceType: string | null
  provenance?: string
  eventType: string
  counterparty: string | null
  expectationId: string | null
  expectedDate: string | null
  /** Set on the collapsed modeled card. */
  modeledCount?: number
}

const shiftDateDays = (iso: string, days: number): string => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return iso
  const date = new Date(Date.UTC(y, m - 1, d))
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

const daysBetween = (from: string, to: string): number => {
  const parse = (iso: string) => {
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
    return y && m && d ? Date.UTC(y, m - 1, d) : null
  }
  const a = parse(from)
  const b = parse(to)
  if (a == null || b == null) return 0
  return Math.round((b - a) / 86_400_000)
}

/**
 * Where a row lands when moved to another week.
 *
 * Keeps its position WITHIN the bucket rather than snapping to the week start:
 * a bill expected on the Friday of one week should be expected on the Friday of
 * the target week, not dragged forward to the Monday. The grid buckets rolling
 * 7-day windows anchored at forecast_start, so the offset is measured against
 * this cell's own bucket start, never a calendar Monday.
 */
export const targetDateForWeek = (
  fromDate: string,
  bucketStart: string,
  targetWeekStart: string,
): string => {
  const offset = Math.min(6, Math.max(0, daysBetween(bucketStart, fromDate)))
  return shiftDateDays(targetWeekStart, offset)
}

/** Groups anonymous modeled rows by the GL account behind them. */
const modeledGroupKey = (row: QuickBooksCashPlanningRow): string =>
  trimStr(row.label || '') || trimStr(row.counterparty_name || '') || row.event_type

const detailText = (row: Record<string, unknown>, key: string): string =>
  trimStr(typeof row[key] === 'string' || typeof row[key] === 'number' ? String(row[key]) : '')

const detailSignedAmount = (row: Record<string, unknown>): number => {
  const value = Number(detailText(row, 'signed_amount'))
  return Number.isFinite(value) ? value : 0
}

export function CfoCashWeekDrill({
  appApi,
  workbookId,
  rowKey,
  rowLabel,
  weekStart,
  weekLabel,
  rows,
  allRows,
  weekOptions,
  canEdit = true,
  scenarioKey = 'base',
  summaryId,
  currentRead,
  detailHandle,
  onMove,
  onReset,
  onChanged,
  onClose,
}: CfoCashWeekDrillProps) {
  const [busyKey, setBusyKey] = useState('')
  const [error, setError] = useState('')
  const [modeledOpen, setModeledOpen] = useState(false)
  const [actualDetails, setActualDetails] = useState<QuickBooksCashPlanningDetailsResponse | null>(
    null,
  )
  const [actualDetailRows, setActualDetailRows] = useState<Array<Record<string, unknown>>>([])
  const [actualDetailLoading, setActualDetailLoading] = useState(false)
  const [actualDetailError, setActualDetailError] = useState('')

  const groups = useMemo<DrillGroup[]>(() => {
    const byDoc = new Map<string, DrillGroup>()
    const named: DrillGroup[] = []
    const modeled: QuickBooksCashPlanningRow[] = []
    const documentScope = allRows && allRows.length ? allRows : rows

    for (const row of rows) {
      const amount = signedCashAmount(row)
      const provenance = cashRowProvenance(row)
      const doc = cashPlanningDocumentIdFor(row)
      const expectedDate = trimStr(row.expected_date || '') || null

      if (doc && provenance === 'document') {
        const existing = byDoc.get(doc)
        if (existing) {
          existing.amount += amount
          if (expectedDate && (!existing.expectedDate || expectedDate < existing.expectedDate)) {
            existing.expectedDate = expectedDate
          }
          continue
        }
        byDoc.set(doc, {
          key: `doc:${doc}`,
          title: trimStr(row.counterparty_name || '') || row.label,
          amount,
          documentTotal: documentTotalFor(documentScope, doc),
          detail: rowKey === 'collections' ? `Invoice ${doc}` : `Bill ${doc}`,
          chips: describeCashRow(row),
          warnings: cashRowWarnings(row),
          editable: row.editable !== false,
          documentId: doc,
          sourceType: row.source_type || null,
          provenance,
          eventType: row.event_type,
          counterparty: row.counterparty_name || null,
          expectationId: null,
          expectedDate,
        })
        continue
      }

      if (provenance === 'modeled') {
        modeled.push(row)
        continue
      }

      // user + recurring + actual: named, one row each.
      named.push({
        key: `row:${row.id}`,
        title: trimStr(row.counterparty_name || '') || row.label,
        amount,
        documentTotal: null,
        detail:
          provenance === 'user'
            ? 'Your adjustment'
            : trimStr(row.forecast_basis || '') || 'Modeled from payment history',
        chips: describeCashRow(row),
        warnings: cashRowWarnings(row),
        // A recurring occurrence is movable: `plan_identity` carries the
        // date-free stream_key, so the server can claim THIS occurrence and
        // leave the rest of the schedule alone. The user's own rows are always
        // movable. Everything else (modeled aggregates) has no stable identity
        // to claim, so an expectation against one would add cash rather than
        // re-time it.
        editable:
          (provenance === 'user' || provenance === 'recurring') && row.editable !== false,
        // For a recurring row this is the stream_key, not a document id.
        documentId: doc || trimStr(row.plan_identity || '') || null,
        sourceType: row.source_type || null,
        provenance,
        eventType: row.event_type,
        counterparty: row.counterparty_name || null,
        expectationId: row.expectation_id || null,
        expectedDate,
      })
    }

    const result = [...byDoc.values(), ...named].sort(
      (a, b) => Math.abs(b.amount) - Math.abs(a.amount),
    )

    if (modeled.length) {
      const accounts = new Set(modeled.map(modeledGroupKey))
      result.push({
        key: 'modeled:plug',
        title: rowKey === 'collections' ? 'Projected collections' : `Projected ${rowLabel.toLowerCase()}`,
        amount: modeled.reduce((sum, row) => sum + signedCashAmount(row), 0),
        documentTotal: null,
        detail: describeModeledPlug(rowLabel, accounts.size),
        chips: [],
        warnings: [],
        editable: false,
        documentId: null,
        sourceType: null,
        eventType: '',
        counterparty: null,
        expectationId: null,
        expectedDate: null,
        modeledCount: accounts.size,
      })
    }
    return result
  }, [allRows, rowKey, rowLabel, rows])

  const modeledRows = useMemo(
    () => rows.filter((row) => cashRowProvenance(row) === 'modeled'),
    [rows],
  )
  const hasPostedActualRows = useMemo(
    () => rows.some((row) => row.row_kind === 'posted_actual'),
    [rows],
  )

  useEffect(() => {
    const wb = trimStr(workbookId)
    const summary = trimStr(summaryId || '')
    if (!wb || !summary || !detailHandle?.row_count || !hasPostedActualRows) {
      setActualDetails(null)
      setActualDetailRows([])
      setActualDetailLoading(false)
      setActualDetailError('')
      return
    }
    const controller = new AbortController()
    setActualDetails(null)
    setActualDetailRows([])
    setActualDetailLoading(true)
    setActualDetailError('')
    appApi
      .getQuickBooksCashPlanningDetails(
        wb,
        {
          summary_id: summary,
          week: weekStart,
          category_id: rowKey,
          limit: 100,
          ...currentRead,
        },
        controller.signal,
      )
      .then((response) => {
        if (controller.signal.aborted) return
        setActualDetails(response)
        setActualDetailRows(response.rows)
      })
      .catch((err: any) => {
        if (controller.signal.aborted || err?.name === 'AbortError') return
        setActualDetailError(
          trimStr(err?.body || err?.message || err) || 'Transaction evidence could not load.',
        )
      })
      .finally(() => {
        if (!controller.signal.aborted) setActualDetailLoading(false)
      })
    return () => controller.abort()
  }, [appApi, currentRead, detailHandle?.row_count, hasPostedActualRows, rowKey, summaryId, weekStart, workbookId])

  const loadMoreActualDetails = async () => {
    const wb = trimStr(workbookId)
    const summary = trimStr(summaryId || '')
    const cursor = actualDetails?.next_cursor
    if (!wb || !summary || !cursor || actualDetailLoading) return
    setActualDetailLoading(true)
    setActualDetailError('')
    try {
      const response = await appApi.getQuickBooksCashPlanningDetails(wb, {
        summary_id: summary,
        week: weekStart,
        category_id: rowKey,
        cursor,
        limit: 100,
        ...currentRead,
      })
      setActualDetails(response)
      setActualDetailRows((current) => [...current, ...response.rows])
    } catch (err: any) {
      setActualDetailError(
        trimStr(err?.body || err?.message || err) || 'More transaction evidence could not load.',
      )
    } finally {
      setActualDetailLoading(false)
    }
  }

  const moveWeeks = (group: DrillGroup, weeks: number) =>
    move(group, shiftDateDays(group.expectedDate || weekStart, weeks * 7))

  const move = async (group: DrillGroup, targetDate: string) => {
    const wb = trimStr(workbookId)
    if (!wb || busyKey) return
    const fromDate = group.expectedDate || weekStart
    // A document move re-times the WHOLE document — the server drops every lag
    // leg across all 13 weeks, so the clicked cell's subtotal would book a
    // fraction and delete the remainder.
    const amount = group.documentTotal ?? group.amount
    const sourceType = expectationSourceTypeFor(group.sourceType, rowKey, group.provenance)
    setBusyKey(group.key)
    setError('')
    try {
      if (onMove) {
        await onMove({
          expectationId: group.expectationId,
          rowKey,
          scenarioKey,
          eventType: group.eventType,
          counterparty: group.counterparty,
          title: group.title,
          documentId: group.documentId,
          sourceType: group.sourceType,
          provenance: group.provenance,
          fromDate,
          targetDate,
          amount,
        })
      } else if (group.expectationId) {
        await appApi.updateQuickBooksCashPlanningExpectation(wb, group.expectationId, {
          expected_date: targetDate,
        })
      } else {
        await appApi.createQuickBooksCashPlanningExpectation(wb, {
          scenario_key: scenarioKey,
          event_type: group.eventType,
          counterparty_name: group.counterparty,
          description: `${group.title} — moved from ${fromDate}`,
          expected_date: targetDate,
          expected_amount: Math.abs(amount),
          source_type: sourceType,
          source_id: group.documentId,
          // The claim must name the occurrence being moved, not the target.
          source_payload_json:
            sourceType === 'recurring_stream' ? { claim: { occurrence: fromDate } } : undefined,
          note: 'Moved from the 13-week cash table',
        })
      }
      if (!onMove) onChanged?.()
    } catch (err: any) {
      setError(
        Number(err?.status) === 403
          ? 'Read-only session — try this on your own books →'
          : trimStr(err?.body || err?.message || err) || 'Could not move this item.',
      )
    } finally {
      setBusyKey('')
    }
  }

  const headings = ['Item', 'Detail', 'Expected', 'Amount']
  if (canEdit) headings.push('Move')

  return (
    <section className="cfo-ar-card" data-testid="cfo-cash-week-drill" style={{ marginTop: 12 }}>
      <div className="cfo-ar-section-bar">
        <div className="cfo-ar-section-bar-title">
          {rowLabel} · {weekLabel}
        </div>
        <div className="cfo-ar-section-bar-end">
          <span className="cfo-ar-section-bar-meta">
            {groups.length} item{groups.length === 1 ? '' : 's'}
          </span>
          {onClose ? (
            <button type="button" className="cfo-shell-chip" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </div>
      {error ? (
        <div className="cfo-shell-notice error" role="alert">
          {error}
        </div>
      ) : null}
      {groups.length ? (
        // `table-layout: fixed` + explicit widths: Detail now carries the
        // evidence sentence, and with auto layout it collapsed to a narrow
        // column that wrapped one word per line while Move stayed wide.
        <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            minWidth: 720,
            borderCollapse: 'collapse',
            tableLayout: 'fixed',
          }}
        >
          <colgroup>
            <col style={{ width: '20%' }} />
            <col style={{ width: '34%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '13%' }} />
            {canEdit ? <col style={{ width: '22%' }} /> : null}
          </colgroup>
          <thead>
            <tr>
              {headings.map((heading) => (
                <th
                  key={heading}
                  style={{
                    textAlign: heading === 'Amount' ? 'right' : 'left',
                    padding: '6px 8px',
                    fontSize: 12,
                    color: 'var(--muted)',
                    borderBottom: '1px solid var(--line, #e5e0d8)',
                  }}
                >
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const isModeledPlug = group.key === 'modeled:plug'
              const partial =
                group.documentTotal != null &&
                Math.abs(Math.abs(group.documentTotal) - Math.abs(group.amount)) >= 1
              return (
                <tr key={group.key} data-testid={`cfo-cash-drill-row-${group.key}`}>
                  <td style={{ padding: '6px 8px', fontSize: 13, fontWeight: 600 }}>
                    {group.title}
                    {isModeledPlug ? (
                      <>
                        {' '}
                        <button
                          type="button"
                          className="cfo-shell-chip"
                          onClick={() => setModeledOpen((open) => !open)}
                          aria-expanded={modeledOpen}
                        >
                          {modeledOpen ? 'Hide accounts' : 'Show accounts'}
                        </button>
                      </>
                    ) : null}
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: 12.5, color: 'var(--muted)' }}>
                    {group.detail}
                    {group.chips.length ? (
                      <div style={{ fontSize: 12, marginTop: 2 }}>{group.chips.join(' · ')}</div>
                    ) : null}
                    {group.warnings.map((warning) => (
                      <div
                        key={warning}
                        role="alert"
                        style={{ fontSize: 12, marginTop: 3, color: 'var(--danger, #b3261e)' }}
                      >
                        {warning}
                      </div>
                    ))}
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: 12.5, whiteSpace: 'nowrap' }}>
                    {group.expectedDate || weekStart}
                  </td>
                  <td
                    style={{
                      padding: '6px 8px',
                      fontSize: 13,
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatCurrencyCompact(group.amount)}
                    {partial ? (
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                        of {formatCurrencyCompact(group.documentTotal as number)} total
                      </div>
                    ) : null}
                  </td>
                  {canEdit ? (
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                      {group.editable ? (
                        <>
                          <button
                            type="button"
                            className="cfo-shell-chip"
                            disabled={busyKey === group.key}
                            title={
                              partial
                                ? `Moves the whole ${formatCurrencyCompact(group.documentTotal as number)} a week earlier`
                                : 'Expect this a week earlier — the plan recomputes'
                            }
                            onClick={() => moveWeeks(group, -1)}
                          >
                            ← Pull a week
                          </button>{' '}
                          <button
                            type="button"
                            className="cfo-shell-chip"
                            disabled={busyKey === group.key}
                            title={
                              partial
                                ? `Moves the whole ${formatCurrencyCompact(group.documentTotal as number)} a week later`
                                : 'Expect this a week later — the plan recomputes'
                            }
                            onClick={() => moveWeeks(group, 1)}
                          >
                            Defer a week →
                          </button>{' '}
                          {weekOptions && weekOptions.length ? (
                            <select
                              aria-label={`Move ${group.title} to a week`}
                              className="cfo-shell-chip"
                              disabled={busyKey === group.key}
                              value=""
                              onChange={(event) => {
                                const target = event.target.value
                                event.target.value = ''
                                if (target) {
                                  move(
                                    group,
                                    targetDateForWeek(
                                      group.expectedDate || weekStart,
                                      weekStart,
                                      target,
                                    ),
                                  )
                                }
                              }}
                            >
                              <option value="">Move to…</option>
                              {weekOptions.map((option) => (
                                <option
                                  key={option.weekStart}
                                  value={option.weekStart}
                                  disabled={option.weekStart === weekStart}
                                >
                                  {option.label}
                                  {option.weekStart === weekStart ? ' (current)' : ''}
                                </option>
                              ))}
                            </select>
                          ) : null}
                          {onReset && group.expectationId ? (
                            <>
                              {' '}
                              <button
                                type="button"
                                className="cfo-shell-chip"
                                disabled={busyKey === group.key}
                                title="Drop your adjustment and go back to the model's own timing"
                                onClick={async () => {
                                  if (!group.expectationId) return
                                  setBusyKey(group.key)
                                  setError('')
                                  try {
                                    await onReset(group.expectationId)
                                  } catch (err: any) {
                                    setError(
                                      trimStr(err?.body || err?.message || err) ||
                                        'Could not reset this item.',
                                    )
                                  } finally {
                                    setBusyKey('')
                                  }
                                }}
                              >
                                Reset
                              </button>
                            </>
                          ) : null}
                        </>
                      ) : (
                        <span style={{ color: 'var(--muted)' }}>—</span>
                      )}
                    </td>
                  ) : null}
                </tr>
              )
            })}
            {modeledOpen
              ? modeledRows.map((row) => (
                  <tr key={`modeled-detail:${row.id}`} data-testid="cfo-cash-drill-modeled-detail">
                    <td style={{ padding: '4px 8px 4px 24px', fontSize: 12.5, color: 'var(--muted)' }}>
                      {modeledGroupKey(row)}
                    </td>
                    <td style={{ padding: '4px 8px', fontSize: 12, color: 'var(--muted)' }}>
                      {describeCashRow(row).join(' · ') || trimStr(row.forecast_basis || '')}
                    </td>
                    <td style={{ padding: '4px 8px', fontSize: 12, whiteSpace: 'nowrap', color: 'var(--muted)' }}>
                      {row.expected_date || weekStart}
                    </td>
                    <td
                      style={{
                        padding: '4px 8px',
                        fontSize: 12.5,
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        color: 'var(--muted)',
                      }}
                    >
                      {formatCurrencyCompact(signedCashAmount(row))}
                    </td>
                    {canEdit ? <td /> : null}
                  </tr>
                ))
              : null}
          </tbody>
        </table>
        </div>
      ) : (
        <p style={{ color: 'var(--muted)', padding: '6px 8px' }}>No individual items behind this cell.</p>
      )}
      {hasPostedActualRows && detailHandle?.row_count ? (
        <div
          data-testid="cfo-cash-posted-evidence"
          style={{ borderTop: '1px solid var(--line, #e5e0d8)', padding: '10px 8px 4px' }}
        >
          <div
            style={{
              alignItems: 'baseline',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              justifyContent: 'space-between',
              marginBottom: 6,
            }}
          >
            <strong style={{ fontSize: 13 }}>{currentRead ? 'Same-source cash detail' : 'Posted transaction evidence'}</strong>
            <span style={{ color: 'var(--muted)', fontSize: 12 }}>
              {actualDetails
                ? `${actualDetails.expected_row_count} ${currentRead ? 'captured facts and assumptions' : 'reconciled rows'} · ${actualDetails.source.replaceAll('_', ' ')}`
                : `${detailHandle.row_count} ${currentRead ? 'captured facts and assumptions' : 'reconciled rows'}`}
            </span>
          </div>
          {actualDetailError ? (
            <div className="cfo-shell-notice error" role="alert">
              {actualDetailError}
            </div>
          ) : null}
          {actualDetailRows.length ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', minWidth: 620, width: '100%' }}>
                <thead>
                  <tr>
                    {['Date', 'Transaction', 'Counterparty', 'Account', 'Amount'].map((heading) => (
                      <th
                        key={heading}
                        style={{
                          borderBottom: '1px solid var(--line, #e5e0d8)',
                          color: 'var(--muted)',
                          fontSize: 12,
                          padding: '6px 8px',
                          textAlign: heading === 'Amount' ? 'right' : 'left',
                        }}
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {actualDetailRows.map((row, index) => (
                    <tr key={`${detailText(row, 'transaction_key') || 'evidence'}:${index}`}>
                      <td style={{ fontSize: 12.5, padding: '6px 8px', whiteSpace: 'nowrap' }}>
                        {detailText(row, 'txn_date') || weekStart}
                      </td>
                      <td style={{ fontSize: 12.5, padding: '6px 8px' }}>
                        {detailText(row, 'transaction_type') || 'Cash transaction'}
                      </td>
                      <td style={{ fontSize: 12.5, padding: '6px 8px' }}>
                        {detailText(row, 'counterparty') || '—'}
                      </td>
                      <td style={{ fontSize: 12.5, padding: '6px 8px' }}>
                        {detailText(row, 'account_name') || '—'}
                      </td>
                      <td
                        style={{
                          fontSize: 12.5,
                          fontVariantNumeric: 'tabular-nums',
                          padding: '6px 8px',
                          textAlign: 'right',
                        }}
                      >
                        {formatCurrencyCompact(detailSignedAmount(row))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : actualDetailLoading ? (
            <p style={{ color: 'var(--muted)', fontSize: 12.5 }}>Loading reconciled evidence…</p>
          ) : null}
          {actualDetails?.next_cursor ? (
            <button
              type="button"
              className="cfo-shell-chip"
              disabled={actualDetailLoading}
              onClick={loadMoreActualDetails}
              style={{ marginTop: 8 }}
            >
              {actualDetailLoading ? 'Loading…' : 'Load more transactions'}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

export default CfoCashWeekDrill
